import React, { useState } from "react";
import { cancelAccountDeletionByLink } from "../api/endpoints";
import { VerificationExpiredError } from "../api/config";
import DeletionShell from "../components/account/DeletionShell";

type Phase = "ready" | "sending" | "cancelled" | "invalid" | "failed";

/** Reads `#token=…` and removes it from the address bar and history entry. */
function takeTokenFromHash(): string {
  if (typeof window === "undefined") return "";
  const token = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token") ?? "";
  if (token) window.history.replaceState(null, "", window.location.pathname);
  return token;
}

/**
 * Landing page of the "cancel deletion" link in the scheduled / reminder
 * emails, for owners who cannot or do not want to sign in. Opening it changes
 * nothing; pressing the button withdraws the scheduled deletion.
 */
export default function DeleteAccountCancelPage(): React.JSX.Element {
  const [token] = useState(takeTokenFromHash);
  const [phase, setPhase] = useState<Phase>(token ? "ready" : "invalid");

  const handleCancel = async (): Promise<void> => {
    setPhase("sending");
    try {
      await cancelAccountDeletionByLink(token);
      setPhase("cancelled");
    } catch (error) {
      setPhase(error instanceof VerificationExpiredError ? "invalid" : "failed");
    }
  };

  return (
    <DeletionShell maxWidth="560px">
      <div className="stack-4">
        {(phase === "ready" || phase === "sending") && (
          <>
            <h1 className="t-h2" style={{ margin: 0 }}>Keep your Kivo Skin account?</h1>
            <p className="t-small" style={{ margin: 0 }}>
              Your account is scheduled for deletion. Cancel the deletion and your account and all
              its data stay exactly as they are.
            </p>
            <button
              type="button"
              className="btn btn--primary"
              disabled={phase === "sending"}
              onClick={() => { void handleCancel(); }}
              style={{ alignSelf: "flex-start" }}
            >
              {phase === "sending" ? "Cancelling…" : "Cancel the deletion"}
            </button>
          </>
        )}

        {phase === "cancelled" && (
          <div className="stack-3" role="status">
            <h1 className="t-h2" style={{ margin: 0 }}>Deletion cancelled</h1>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              Your account will not be deleted. We have emailed you a confirmation.
            </p>
            <a className="btn btn--primary" href="/login" style={{ alignSelf: "flex-start" }}>Sign in</a>
          </div>
        )}

        {phase === "invalid" && (
          <>
            <h1 className="t-h2" style={{ margin: 0 }}>This link no longer works</h1>
            <p className="t-small t-mute" style={{ margin: 0 }}>
              It may already have been used, or the deletion was cancelled or carried out. If your
              account still exists, sign in and cancel the deletion from the delete-account page.
            </p>
            <a className="btn btn--primary" href="/delete-account" style={{ alignSelf: "flex-start" }}>
              Open the delete-account page
            </a>
          </>
        )}

        {phase === "failed" && (
          <>
            <h1 className="t-h2" style={{ margin: 0 }}>Something went wrong</h1>
            <p role="alert" className="t-small t-mute" style={{ margin: 0 }}>
              We couldn&apos;t cancel the deletion right now. Your link is still valid &mdash; reload
              the page and try again, or write to support@kivo.skin.
            </p>
          </>
        )}
      </div>
    </DeletionShell>
  );
}
